import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import * as roleService from "../../services/role.services";
import CustomPagination from "../../common/custompagination";
import Sorting from "../../common/sorting";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { capitalizeFirstLetter, formateDateWithMonth, handleServerValidations, TrimText, usePermission } from "../../../../utils/commonfunction";
import { Placeholder, Tooltip, Whisper } from "rsuite";

const RoleTable = () => {
  const { can } = usePermission();
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [statsupdate, setStatusUpdate] = useState(false);
  const [datalength, setDataLength] = useState("");
  const [itemperpage] = useState(10);
  const [sorting, setSorting] = useState({});
  const [defaultSorting, setDefaultSorting] = useState(true);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [search, setSearch] = useState({});
  const [globalsearch, setGlobalSearch] = useState("");

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "Sub Admins", url: "/admin/staff/list/1" },
    { title: "Roles", url: "" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoader(true);
      const formData = new FormData();
      formData.append("page", params.pgno);
      formData.append("per_page", itemperpage);
      formData.append("sort", JSON.stringify(sorting));
      formData.append("search", JSON.stringify(search));
      formData.append("global_search", globalsearch);
      roleService.List(formData).then((data) => {
        setDataLength(data?.data?.total_records || "");
        setList(data?.data?.list || []);
        setPage(data?.data?.page || 1);
        setLoader(false);
      }).catch(() => setLoader(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [location, statsupdate, sorting, search, globalsearch, params.pgno]);

  function Deletefunction(data) {
    Swal.fire({
      customClass: "swal-wide", title: t("msg_are_you_sure"), text: t("you_not_be_able_to_revert_this"),
      icon: "warning", showCancelButton: true, cancelButtonText: t("btn_cancel"),
      confirmButtonColor: "#403fad", cancelButtonColor: "#f1388b", confirmButtonText: t("btn_yes_delete"),
    }).then((result) => {
      if (result.isConfirmed) {
        roleService.Delete({ o_id: data._id }).then((response) => {
          setStatusUpdate(!statsupdate);
          Swal.fire({ icon: response.success ? "success" : "error", text: response.success ? response.message : handleServerValidations(response), ...SWAL_SETTINGS });
        });
      }
    });
  }

  const handleSort = (e, column) => {
    setDefaultSorting(false);
    setSorting({ order: e.target.classList.contains("assc") ? -1 : 1, column });
  };

  const prepareSearch = (key, value) => {
    let sr = { ...search };
    if (String(value).length > 0) sr[key] = value;
    else delete sr[key];
    setSearch(sr);
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label">Role Management</h6>
              <div className="d-flex align-items-center">
                <div className="form-group mb-0 me-3">
                  <div className="form-group mb-0 rangepicker_container filter_design">
                    <i className="fa fa-search calender_icon"></i>
                    <input type="search" className="form-control" value={globalsearch}
                      placeholder={t("input_placeholder_search")} onChange={(e) => setGlobalSearch(e.target.value)} />
                  </div>
                </div>
                {can("/admin/role/add") && (
                  <button className="btn ripple btn-main-primary signbtn" onClick={() => navigate("/admin/role/add")}>
                    {t("btn_add_new")}
                  </button>
                )}
              </div>
            </div>

            {loader ? (
              <Placeholder.Paragraph className="w-100" rowHeight={30} rowSpacing={5} active rows={7} />
            ) : (
              <>
                <div className="table-responsive min-h-400">
                  <table className="table table-bordered card-table text-nowrap w-100">
                    <thead>
                      <tr>
                        <th>{t("list_heading_sno")}</th>
                        <th>Title</th>
                        <th>Permissions Count</th>
                        <th className="created_head">
                          <div className="sorting_column">
                            <span>{t("list_heading_created_date")}</span>
                            <Sorting sort={sorting} handleSort={handleSort} defaultSorting={defaultSorting} column="createdAt" />
                          </div>
                        </th>
                        {(can("/admin/role/edit") || can("/admin/role/delete")) && (
                          <th className="action_head">{t("list_heading_action")}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {list.length ? list.map((row, i) => (
                        <tr key={row._id || i}>
                          <td>{(page - 1) * itemperpage + i + 1}</td>
                          <td>{row?.title ? capitalizeFirstLetter(TrimText(row.title, 50)) : "N/A"}</td>
                          <td>{row?.permissionIDs?.length ?? row?.permissions?.length ?? 0}</td>
                          <td>{row?.createdAt ? formateDateWithMonth(row.createdAt) : "N/A"}</td>
                          {(can("/admin/role/edit") || can("/admin/role/delete")) && (
                            <td>
                              <div className="btn-toolbar table-actions">
                                <div className="btn-group btn-group-sm">
                                  {can("/admin/role/edit") && (
                                    <Whisper placement="top" trigger="hover" speaker={<Tooltip style={{ fontWeight: "bold" }}>Edit</Tooltip>}>
                                      <button type="button" className="btn edit" onClick={() => navigate(`/admin/role/list/${params.pgno || 1}/edit/${row._id}`)}>
                                        <i className="ri-pencil-fill"></i>
                                      </button>
                                    </Whisper>
                                  )}
                                  {can("/admin/role/delete") && (
                                    <Whisper placement="top" trigger="hover" speaker={<Tooltip style={{ fontWeight: "bold" }}>Delete</Tooltip>}>
                                      <button type="button" className="btn delete" onClick={() => Deletefunction(row)}>
                                        <i className="ri-delete-bin-6-fill"></i>
                                      </button>
                                    </Whisper>
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      )) : (
                        <tr><td colSpan={5} className="text-center">{t("msg_no_records")}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div><b>{t("msg_total_records")} : {datalength || "0"}</b></div>
                {datalength && Number(datalength) > 0 && (
                  <CustomPagination datalength={datalength} itemperpage={itemperpage} currentPage={page}
                    setPage={setPage} pageRoute={[{ name: "", path: "/admin/role/list" }]} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleTable;
